import React, { useState, useCallback, useRef } from "react";
import { FaShare, FaCopy } from "react-icons/fa";
import { renderCardIcon, simulantIcon } from "../common/IconLibrary";
import ContextMenu from "../common/ContextMenu";
import { useToast } from "../common/ToastContext";
import { duplicatePrompt } from "../../services/promptService";

const SimulantCard = ({
  item = {},
  onClick,
  hideContextMenu = false,
  height = null,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [isContextMenuInteraction, setIsContextMenuInteraction] =
    useState(false);
  const parentRef = useRef(null);
  const { showToast } = useToast();

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setContextMenu({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      setIsContextMenuInteraction(true);
    }
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setIsContextMenuInteraction(false);
  }, []);

  const handleCardClick = (event) => {
    if (!isContextMenuInteraction) {
      onClick(event);
    }
  };

  const handleDuplicate = useCallback(async () => {
    try {
      if (!item?.key) {
        showToast("Cannot duplicate: Invalid simulant key", "error");
        return;
      }
      const duplicated = await duplicatePrompt(item.key);
      closeContextMenu();
      showToast(
        `Simulant duplicated successfully as ${duplicated.name}`,
        "success"
      );
      window.dispatchEvent(
        new CustomEvent("simulantCreated", {
          detail: null, // don't send the item along, otherwise, it will auto-open
        })
      );
    } catch (error) {
      console.error("Error duplicating simulant:", error);
      showToast("Failed to duplicate simulant", "error");
    }
  }, [item?.key, closeContextMenu, showToast]);

  const contextMenuItems = [
    {
      label: "Share",
      icon: FaShare,
      onClick: () => {
        const url = `${window.location.origin}/simulants/?key=${item.key}`;
        navigator.clipboard.writeText(url);
        closeContextMenu();
        showToast("Link copied to clipboard", "success");
      },
    },
    item?.key && {
      label: "Duplicate",
      icon: FaCopy,
      onClick: handleDuplicate,
    },
  ].filter(Boolean);

  return (
    <div
      className={`card-glass ${height ? "" : "h-full"}`}
      style={height ? { height } : {}}
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      ref={parentRef}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center mb-2">
          <span className="text-gray-300 mr-2">
            {renderCardIcon(simulantIcon, 24)}
          </span>
          <div className="flex items-center h-[54px]">
            <h3 className="text-xl font-semibold text-gray-100 text-pretty">
              {item?.name}
            </h3>
          </div>
        </div>
        <div className="relative group flex-grow">
          <p className="text-gray-300 mb-4 line-clamp-3 overflow-hidden">
            {item?.description}
          </p>
          <div className="hint-glass">{item?.description}</div>
        </div>
        <div className="mt-auto">
          <div className="flex flex-wrap mt-2 min-h-8">
            {item?.tags?.map(
              (tag) =>
                tag && (
                  <span
                    key={tag}
                    className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full mr-2 mb-2"
                  >
                    {tag}
                  </span>
                )
            )}
          </div>
          {item?.deleted && (
            <div className="mt-2 text-red-500 text-sm font-semibold">
              Deleted{" "}
              {item?.updated_at
                ? new Date(item.updated_at).toLocaleString()
                : ""}
            </div>
          )}
        </div>
        {!hideContextMenu && contextMenu && (
          <ContextMenu
            items={contextMenuItems}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={closeContextMenu}
            parentRef={parentRef}
          />
        )}
      </div>
    </div>
  );
};

export default SimulantCard;
